import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SearchIcon from '@mui/icons-material/Search';
import { KeyValueGrid } from './ExpandableLineItemParts.jsx';
import VarianceEvidencePicker from './VarianceEvidencePicker.jsx';
import BacAccountLookupDialog from './BacAccountLookupDialog.jsx';
import { money } from './drawerFormat.js';
import { isSyntheticBac } from './syntheticBac.js';
import { buildLinkAction, rowKeyFromContext } from './productDetailLinkAction.js';
import {
  accountOptionsFromAppsheet,
  bindingFieldsFromContext,
  evidenceOverrideFromContext,
  firstNonBlank,
  isPopcornParentProductCode,
  preferredAccountForProduct,
} from './varianceActionDialogModel.js';
import {
  accountConfidenceLabel,
  accountExplainabilityText,
} from './productWarningState.js';

const normalizedText = (value) => String(value || '').trim().toLowerCase();
const uniqueAccountOptions = (rows = []) => [...new Map(rows.map((row) => [
  row.salesforceAccountId || row.id || row.salesforceAccountName,
  row,
])).values()];

const accountFieldsFromEvidence = (evidence = {}) => ({
  salesforceAccountId: firstNonBlank(
    evidence.salesforceAccountId,
    evidence.row?.salesforceAccountId,
    evidence.row?.accountId,
  ),
  salesforceAccountName: firstNonBlank(
    evidence.salesforceAccountName,
    evidence.row?.salesforceAccountName,
    evidence.row?.accountName,
    evidence.row?.dealerName,
  ),
});

const accountOptionForEvidence = (evidence, accountOptions = []) => {
  const fields = accountFieldsFromEvidence(evidence);
  const accountId = normalizedText(fields.salesforceAccountId);
  const accountName = normalizedText(fields.salesforceAccountName);
  if (!accountId && !accountName) return null;
  return accountOptions.find((account) => (
    (accountId && (
      normalizedText(account.salesforceAccountId) === accountId
      || normalizedText(account.id) === accountId
    ))
    || (accountName && normalizedText(account.salesforceAccountName) === accountName)
  )) || null;
};

const accountOptionFromCurrentBinding = ({ bindingFields = {}, evidenceOverride = {} } = {}) => {
  const salesforceAccountId = firstNonBlank(bindingFields.salesforceAccountId);
  const salesforceAccountName = firstNonBlank(bindingFields.salesforceAccountName);
  const hasLinkedEvidence = Boolean(evidenceOverride.orderItemId || evidenceOverride.referenceId);
  const isTrustedManualBinding = String(bindingFields.bindingMethod || '').toLowerCase().includes('manual')
    || Number(bindingFields.bindingConfidence) >= 90;
  if (!salesforceAccountId && !(salesforceAccountName && (hasLinkedEvidence || isTrustedManualBinding))) return null;
  const id = salesforceAccountId || salesforceAccountName;
  return {
    id,
    salesforceAccountId,
    salesforceAccountName,
    peopleSoftId: bindingFields.peopleSoftId || '',
    brandSet: new Set(),
    label: salesforceAccountName || 'Salesforce account',
  };
};

const withCurrentBindingAccount = (accountOptions = [], bindingFields = {}, evidenceOverride = {}) => {
  const currentOption = accountOptionFromCurrentBinding({ bindingFields, evidenceOverride });
  if (!currentOption) return accountOptions;
  const currentId = normalizedText(currentOption.salesforceAccountId || currentOption.id);
  const currentName = normalizedText(currentOption.salesforceAccountName);
  const exists = accountOptions.some((account) => (
    (currentId && (
      normalizedText(account.salesforceAccountId) === currentId
      || normalizedText(account.id) === currentId
    ))
    || (currentName && normalizedText(account.salesforceAccountName) === currentName)
  ));
  return exists ? accountOptions : [currentOption, ...accountOptions];
};

const displayedBindingAccountOption = (context = {}, bindingFields = {}) => {
  const row = context.row || {};
  const accountMatch = row.accountMatch && typeof row.accountMatch === 'object' ? row.accountMatch : {};
  const salesforceAccountId = firstNonBlank(bindingFields.salesforceAccountId, accountMatch.salesforceAccountId);
  const salesforceAccountName = firstNonBlank(
    bindingFields.salesforceAccountName,
    accountMatch.salesforceAccountName,
    row.salesforceAccountName,
    row.accountName,
    row.dealerName,
  );
  const bindingMethod = firstNonBlank(bindingFields.bindingMethod, accountMatch.matchMethod);
  const bindingConfidence = firstNonBlank(bindingFields.bindingConfidence, accountMatch.confidence);
  const isTrustedManualBinding = String(bindingMethod || '').toLowerCase().includes('manual')
    || Number(bindingConfidence) >= 90;
  if (!salesforceAccountId && !(salesforceAccountName && isTrustedManualBinding)) return null;
  const id = salesforceAccountId || salesforceAccountName;
  return {
    id,
    salesforceAccountId,
    salesforceAccountName,
    peopleSoftId: bindingFields.peopleSoftId || accountMatch.peopleSoftId || '',
    brandSet: new Set(),
    label: salesforceAccountName || 'Salesforce account',
  };
};

const detailEntries = (context = {}) => {
  const row = context.row || {};
  const accountMatch = row.accountMatch && typeof row.accountMatch === 'object' ? row.accountMatch : {};
  const bindingFields = bindingFieldsFromContext(context);
  return Object.entries({
    Source: context.sourceLabel || context.source,
    'Product name': context.productName,
    'Product code': context.productCode,
    Price: context.price || context.price === 0 ? money(Number(context.price)) : '',
    Status: context.status,
    'Row key': rowKeyFromContext(context),
    'Row number': row.rowNumber,
    BAC: row.bac,
    'Dealer name': row.dealerName,
    Quantity: row.quantity,
    'Service month': row.serviceMonth,
    'Effective date': row.effectiveDate,
    'Activation date': row.activationDate,
    'Cancellation date': row.cancellationDate,
    'Billing start': row.billingStartDate,
    'Billing end': row.billingEndDate,
    'AppSheet order item': evidenceOverrideFromContext(context).orderItemId,
    'DPE reference': evidenceOverrideFromContext(context).referenceId,
    'Salesforce account': bindingFields.salesforceAccountName,
    'Salesforce account ID': bindingFields.salesforceAccountId,
    'Match label': accountConfidenceLabel(bindingFields.bindingMethod || accountMatch.matchMethod, bindingFields.bindingConfidence || accountMatch.confidence),
    'Match explanation': accountExplainabilityText(row, { action: bindingFields }),
    'Match method': bindingFields.bindingMethod || accountMatch.matchMethod,
    'Match confidence': bindingFields.bindingConfidence || accountMatch.confidence,
  }).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
};

const detailValue = (entries = [], label = '') => (
  entries.find(([entryLabel]) => entryLabel === label)?.[1] || ''
);

const accountOptionFromDisplayedDetails = (entries = []) => {
  const salesforceAccountName = firstNonBlank(detailValue(entries, 'Salesforce account'));
  if (!salesforceAccountName) return null;
  const salesforceAccountId = firstNonBlank(detailValue(entries, 'Salesforce account ID'));
  const bindingMethod = String(detailValue(entries, 'Match method') || '').toLowerCase();
  const bindingConfidence = Number(detailValue(entries, 'Match confidence'));
  const hasLinkedEvidence = Boolean(
    detailValue(entries, 'AppSheet order item')
    || detailValue(entries, 'DPE reference')
  );
  const isTrustedBinding = hasLinkedEvidence || bindingMethod.includes('manual') || bindingConfidence >= 90;
  if (!isTrustedBinding) return null;
  const id = salesforceAccountId || `manual-account:${salesforceAccountName}`;
  return {
    id,
    salesforceAccountId,
    salesforceAccountName,
    peopleSoftId: '',
    brandSet: new Set(),
    label: salesforceAccountName,
  };
};

const ProductDetailDialog = ({
  context,
  currentUserName,
  onClose,
  onLink,
  gmItems = [],
  cpqItems = [],
  dpeItems = [],
  appsheetData = null,
  appSheetAccounts = null,
  runId = '',
}) => {
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountSelectionMode, setAccountSelectionMode] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupAccount, setLookupAccount] = useState(null);
  const evidenceOverride = useMemo(() => evidenceOverrideFromContext(context || {}), [context]);
  const bindingFields = useMemo(() => bindingFieldsFromContext(context || {}), [context]);
  const displayedDetails = useMemo(() => detailEntries(context || {}), [context]);
  const baseAccountOptions = useMemo(() => accountOptionsFromAppsheet(appsheetData, appSheetAccounts), [appsheetData, appSheetAccounts]);
  const accountOptions = useMemo(
    () => withCurrentBindingAccount(
      uniqueAccountOptions(lookupAccount ? [lookupAccount, ...baseAccountOptions] : baseAccountOptions),
      bindingFields,
      evidenceOverride,
    ),
    [baseAccountOptions, bindingFields, evidenceOverride, lookupAccount],
  );
  const displayedBindingAccount = useMemo(
    () => displayedBindingAccountOption(context || {}, bindingFields) || accountOptionFromDisplayedDetails(displayedDetails),
    [context, bindingFields, displayedDetails],
  );
  const selectableAccountOptions = accountOptions.length
    ? accountOptions
    : displayedBindingAccount
      ? [displayedBindingAccount]
      : [];
  const effectiveSelectedAccountId = selectedAccountId || (selectableAccountOptions.length === 1 ? selectableAccountOptions[0].id : '');
  const selectedAccount = selectableAccountOptions.find((account) => account.id === effectiveSelectedAccountId);
  const isLinked = Boolean(evidenceOverride.orderItemId || evidenceOverride.referenceId || bindingFields.salesforceAccountId);
  const canApply = Boolean(selectedEvidence || selectedAccount);
  const sourceBac = firstNonBlank(context?.row?.bac, context?.bac);
  const canLookupBac = Boolean(runId && context?.source === 'cpq' && (!sourceBac || isSyntheticBac(sourceBac)));
  const activeLookupAccount = lookupAccount?.id === effectiveSelectedAccountId ? lookupAccount : null;
  const assignmentNeedsPeopleSoftId = Boolean(activeLookupAccount && !activeLookupAccount.peopleSoftId);

  useEffect(() => {
    setLookupAccount(null);
    setLookupOpen(false);
  }, [context]);

  useEffect(() => {
    setSelectedEvidence(null);
    const currentAccount = accountOptionForEvidence({ ...bindingFields, row: context?.row }, accountOptions);
    const preferredAccount = preferredAccountForProduct({
      productCode: context?.productCode,
      accountOptions,
      currentAccountId: currentAccount?.salesforceAccountId || bindingFields.salesforceAccountId,
      currentAccountName: currentAccount?.salesforceAccountName || bindingFields.salesforceAccountName,
    });
    setSelectedAccountId((current) => {
      if (current && accountOptions.some((account) => account.id === current)) return current;
      return preferredAccount?.id || (isPopcornParentProductCode(context?.productCode) ? '' : currentAccount?.id || '');
    });
    setAccountSelectionMode('');
  }, [accountOptions, bindingFields, context]);

  const handleSelectEvidence = (evidence) => {
    setSelectedEvidence(evidence);
    const matchedAccount = accountOptionForEvidence(evidence, accountOptions);
    const preferredAccount = preferredAccountForProduct({
      productCode: context?.productCode,
      accountOptions,
      currentAccountId: matchedAccount?.salesforceAccountId,
      currentAccountName: matchedAccount?.salesforceAccountName,
    });
    if (preferredAccount || matchedAccount) {
      setSelectedAccountId((preferredAccount || (isPopcornParentProductCode(context?.productCode) ? null : matchedAccount))?.id || '');
      setAccountSelectionMode('evidence');
    } else if (accountSelectionMode === 'evidence') {
      setSelectedAccountId('');
      setAccountSelectionMode('');
    }
  };

  const handleSelectAccount = (event) => {
    setSelectedAccountId(event.target.value);
    setAccountSelectionMode('manual');
    if (lookupAccount && event.target.value !== lookupAccount.id) setLookupAccount(null);
  };

  const handleLookupAccount = (account) => {
    const normalizedAccount = {
      ...account,
      label: account.label || account.salesforceAccountName || account.name || account.id,
    };
    setSelectedEvidence(null);
    setLookupAccount(normalizedAccount);
    setSelectedAccountId(normalizedAccount.id);
    setAccountSelectionMode('manual');
    setLookupOpen(false);
  };

  const handleApply = () => {
    const action = buildLinkAction({
      context,
      selectedEvidence,
      selectedAccount,
      currentUserName,
      accountSelectionMode,
    });
    if (action) onLink(action);
  };

  return (
    <Dialog
      open={Boolean(context)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: { xs: '92vh', md: '86vh' },
          maxHeight: 'calc(100% - 32px)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          {isLinked ? <LinkIcon color="success" /> : <LinkOffIcon color="error" />}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
              {context?.productName || context?.productCode || 'Product details'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {context?.sourceLabel || context?.source || 'Source'}{context?.productCode ? ` - ${context.productCode}` : ''}
            </Typography>
          </Box>
          {context?.status && <Chip size="small" label={context.status} variant="outlined" />}
          {(context?.price || context?.price === 0) && <Chip size="small" label={money(Number(context.price))} color="primary" />}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Stack spacing={1.5} sx={{ pt: 0.5, flex: 1, minHeight: 0 }}>
          <Alert
            severity={isLinked ? 'success' : 'warning'}
            icon={isLinked ? <LinkIcon /> : <LinkOffIcon />}
            sx={{ py: 0.75 }}
          >
            {isLinked
              ? `Current link: ${[
                evidenceOverride.orderItemId && `Order item ${evidenceOverride.orderItemId}`,
                evidenceOverride.referenceId && `Reference ${evidenceOverride.referenceId}`,
                bindingFields.salesforceAccountName || bindingFields.salesforceAccountId,
              ].filter(Boolean).join(' - ')}`
              : 'This product does not have linked AppSheet/DPE/account evidence yet.'}
          </Alert>
          <Box
            sx={{
              minHeight: { xs: 180, md: 220 },
              maxHeight: { xs: 280, md: 340 },
              overflowY: 'auto',
              pr: 0.5,
              pb: 0.5,
            }}
          >
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              Product row details
            </Typography>
            <KeyValueGrid
              entries={displayedDetails}
              gridSx={{ rowGap: 0.85, columnGap: 2, mt: 0.25 }}
              labelSx={{ lineHeight: 1.45 }}
              valueSx={{ lineHeight: 1.45 }}
              valueVariant="body2"
            />
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box sx={{ minWidth: { sm: 220 }, flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                Salesforce account
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Select ownership, or pick AppSheet evidence below to fill it automatically.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flex: 1 }} alignItems="center">
              <FormControl fullWidth size="small" disabled={!selectableAccountOptions.length}>
                <InputLabel id="product-detail-account-label">Salesforce account</InputLabel>
                <Select
                  labelId="product-detail-account-label"
                  label="Salesforce account"
                  value={effectiveSelectedAccountId}
                  onChange={handleSelectAccount}
                >
                  {selectableAccountOptions.map((account) => (
                    <MenuItem key={account.id} value={account.id}>{account.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {canLookupBac && (
                <Button variant="outlined" startIcon={<SearchIcon />} onClick={() => setLookupOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
                  Lookup BAC
                </Button>
              )}
            </Stack>
          </Stack>
          {activeLookupAccount && (
            <Alert severity={assignmentNeedsPeopleSoftId ? 'error' : 'success'}>
              Assign to BAC {activeLookupAccount.bac} - {activeLookupAccount.salesforceAccountName} - PeopleSoft {activeLookupAccount.peopleSoftId || 'missing'}
            </Alert>
          )}
          <Divider />
          <VarianceEvidencePicker
            gmItems={gmItems}
            cpqItems={cpqItems}
            dpeItems={dpeItems}
            appsheetData={appsheetData}
            selectedEvidence={selectedEvidence}
            currentOrderItemId={evidenceOverride.orderItemId}
            currentReferenceId={evidenceOverride.referenceId}
            onSelectEvidence={handleSelectEvidence}
            maxListHeight={{ xs: 360, md: 460 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleApply} disabled={!canApply || assignmentNeedsPeopleSoftId}>
          Apply link
        </Button>
      </DialogActions>
      {lookupOpen && (
        <BacAccountLookupDialog
          open
          runId={runId}
          onClose={() => setLookupOpen(false)}
          onSelect={handleLookupAccount}
        />
      )}
    </Dialog>
  );
};

export { buildLinkAction } from './productDetailLinkAction.js';
export default ProductDetailDialog;
